// Path: ./src/app/pages/CreateFlyerItemPage/CreateFlyerModal/index.tsx
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from "react";
import {
  toastError,
  toastSuccess,
  toastWarn,
} from "../../../../utils/pageUtil/toast";
import {
  BottomLeftWrapper,
  BottomSection,
  ButtonWrapper,
  imageActionButtonStyles,
  LeftSideWrapper,
  ImageCard,
  ImageCardWrapper,
  ImageTagButton,
  ImageEditButton,
  ImageSubTitle,
  RightSideWrapper,
  InheritTextInputField,
  DotDotDivider,
  SalesSectionWrapper,
  PriceWrapper,
  RegularPrice,
  UnitWrapper,
  SmallImageWrapper,
  MultipleImages,
  ImageLoaderWrapper,
  InheritTextInputFieldV2,
  LimitWrapper,
  LimitInput,
  CardItem,
  RankPriceRemarksWrapper,
  ImageCardEmpty,
  SwitchWrapper,
  RankPriceRemarksWrapperV2,
} from "./styled";
import { AutoResizeTextField } from "../../../components/AutoResizeTextField";
import { InputWithButton } from "../../../components/InputWithButton";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";
import { CustomAutocompleteMultiSelect } from "../../../components/CustomAutocomplete";

import {
  BackgroundRemovalJobStatusDto,
  GfFlyerItem,
  GfFlyerItemCategory,
  GfImage,
  GfImageDto,
  GfMultiImage,
  GfPromotionTag,
  GfUnit,
} from "@swagger/typescript-fetch-goflyer";
import {
  CircularProgress,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { adminApiService } from "../../../../utils/api";
import { useLoading } from "../../../../contextApi/LoadingContext";
import { useFlyerItemCategories, usePromotionTags } from "../../../../hooks";
import { useUnits } from "../../../../hooks/useUnits";
import { useUploadOrPickImages } from "../../../../hooks/useUploadOrPickImages";
import { ImageDropZone } from "../../../../components/ImageDropZone";
import { extractEnglishAndChineseV2 } from "../../../../utils/utils";
import { makeStoreDisplayName } from "../../../../utils/dtoUtil/makeStoreDisplayName";
import { ImageSearchModal } from "../../../components/ImageSearchModal";
import ImageTagEditModal from "../../../components/ImageTagEditModal";
import { getMontrealStores } from "../../../../utils/dtoUtil/getMontrealStores";
import { GoFlyerAdminContext } from "../../../../contextApi/context";
import { SmallDealImgCard } from "../../../components/SmallDealImgCard";
import { detectCategoriesFromURL } from "../../../../utils/DetechFlyerItemCategory";
import { SelectSearchWithAddMui } from "../../../components/SelectSearchWithAddMui";
import { UpdateGfFlyerItemDtoWithWebsiteSelection } from "./createFlyerItemWithMetaData";
import { disableWheelOnInput } from "../../../../utils/disableWheelOnInput";
import InfoIcon from "@mui/icons-material/Info";
import { useAuthHeaders } from "../../../../hooks/useAuthHeaders";
import { ButtonWithTooltip } from "../../../components/ButtonWithTooltip/index";
import UrlImagePasteUpload from "../../../components/UrlImagePasteUpload";
import { useLastSelectedDepartment } from "../../../../hooks/useLastSelectedDepartment";
import { ImageEditorModal } from "../../../components/ImageEditorModal";
import { replaceImageInS3Only } from "../../../../utils/replaceImageInS3";
import {
  imageDesignState,
  savedImageData,
} from "../../../components/ImageEditorModal/types";
import { PermissionComponentWrapper } from "../../../components/PermissionComponentWrapper";
import { flyerItemFieldsWithoutDuplicateIdentity } from "../utils/flyerItemFieldsWithoutDuplicateIdentity";
import { useAiImprovementJob } from "../../../../hooks/useAiImprovementJob";
import { AiImproveImageModal } from "../../../components/AiImproveImageModal/index";
import { MuiConfirmationModal } from "../../../components/MuiConfirmationModal";
import {
  fetchAiImprovementJobStatusSafe,
  isAiImprovementTerminal,
} from "../../../../utils/aiImageImprovement";
import {
  saveAiImprovePendingJob,
  clearAiImprovePendingJob,
  clearAiImprovePendingJobByJobId,
  getAiImprovePendingJobsForImages,
} from "../../../../utils/aiImprovementSession";

interface Props {
  updateFlyerItem: UpdateGfFlyerItemDtoWithWebsiteSelection | undefined;
  setUpdateFlyerItem: React.Dispatch<
    React.SetStateAction<UpdateGfFlyerItemDtoWithWebsiteSelection | undefined>
  >;
  onImageTagSaved?: (updatedImage: GfImage) => void;
  /**
   * use this to close the modal window. refresh the list
   */
}

export const CreateFlyerModal = ({
  updateFlyerItem,
  setUpdateFlyerItem,
  onImageTagSaved,
}: Props) => {
  const authHeaders = useAuthHeaders();
  const { showLoadingScreen } = useLoading();
  const { state } = useContext(GoFlyerAdminContext);
  const { t } = useTranslation();
  const { promotionTags } = usePromotionTags();
  const { flyerItemCategories } = useFlyerItemCategories();
  const { unitsList, fetchUnits } = useUnits();
  const flyerItemListId = updateFlyerItem?.flyerItemList?.id;
  const { lastDepartmentIds } = useLastSelectedDepartment(flyerItemListId);
  const [initialPickedImages] = useState<GfImageDto[]>(
    updateFlyerItem?.gfMultiImages?.map((val) => val.gfImage) || [],
  );
  const [isRemovingBackground, setIsRemovingBackground] = useState(false);
  const [showRemoveBgConfirm, setShowRemoveBgConfirm] = useState(false);
  const [aiImproveModalOpen, setAiImproveModalOpen] = useState(false);
  const [aiImproveJobId, setAiImproveJobId] = useState<string | null>(null);
  const [isImprovingWithAi, setIsImprovingWithAi] = useState(false);
  const [isStartingAiImprove, setIsStartingAiImprove] = useState(false);
  const handledAiImproveJobRef = useRef<string | null>(null);
  const hasAttemptedAiRecoverRef = useRef(false);
  const [aiImproveSourceImageId, setAiImproveSourceImageId] = useState<
    string | null
  >(null);
  const aiImproveCandidateImages = useMemo(
    () =>
      updateFlyerItem?.gfMultiImages
        ?.map((m) => m.gfImage)
        .filter((img): img is GfImage => Boolean(img?.id && img?.src)) ?? [],
    [updateFlyerItem?.gfMultiImages],
  );
  const hasAiImproveCandidateImages = aiImproveCandidateImages.length > 0;
  const {
    status: aiImproveStatus,
    error: aiImprovePollError,
    jobNotFound: aiImproveJobNotFound,
    isDone: isAiImproveDone,
    isSuccess: isAiImproveSuccess,
    isFailed: isAiImproveFailed,
    progress: aiImproveProgress,
    newImage: aiImprovedNewImage,
  } = useAiImprovementJob(aiImproveJobId);
  const [isAvailableNow, setIsAvailableNow] = useState(
    updateFlyerItem?.salePrice === 0,
  );
  const [imageEditorOpen, setImageEditorOpen] = useState(false);
  const [editingImageSrc, setEditingImageSrc] = useState<string>("");
  const [editingImage, setEditingImage] = useState<GfImage | null>(null);
  const [imageForTagEdit, setImageForTagEdit] = useState<GfImage | null>(null);
  const [savingEditedImage, setSavingEditedImage] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const hasAutoSelectedRef = useRef(false);
  const {
    handleOnRemoveImage,
    hasMoreGfImage,
    isSearchingImage,
    openSearchImage,
    productImageList,
    isUploadingImage,
    handleOnSearchPressEnter,
    handlePageChangeScroll,
    handleOnImageFileSelect,
    handleOnImageClick,
    handleOnImageSearchModalClose,
    handleOnImageSearchModalOpen,
    setPickedGfImages,
    setProductImageList,
    isDragging,
    getDropZoneProps,
    showReorderModal,
    pendingDropFiles,
    handleReorderConfirm,
    handleReorderCancel,
  } = useUploadOrPickImages({
    onPickedImageChange: async (images: GfImage[]) => {
      if (updateFlyerItem?.id) {
        const multiImages = images?.map((gfImage) => ({
          gfFlyerItem: {} as GfFlyerItem,
          gfImage,
          id: gfImage.id,
        })) as GfMultiImage[];
        setUpdateFlyerItem((prev) => {
          return {
            ...prev,
            gfMultiImages: [...multiImages],
          };
        });
      } else {
        if (images?.length === 1) {
          const image = images[0];
          const TextResult = extractEnglishAndChineseV2(
            image?.fileName || image?.alt || "",
          );

          showLoadingScreen(true);

          const flyerItemResponse =
            await adminApiService.gfFlyerItemAdminControllerFindFlyerItemByImageAndStore(
              images[0]?.id,
              updateFlyerItem?.flyerItemList?.stores[0]?.id,
              authHeaders,
            );

          const flyerItem =
            flyerItemFieldsWithoutDuplicateIdentity(flyerItemResponse);

          showLoadingScreen(false);
          const multiImages = images?.map((gfImage) => ({
            gfFlyerItem: {} as GfFlyerItem,
            gfImage,
            id: gfImage.id,
          })) as GfMultiImage[];
          setUpdateFlyerItem((prev) => {
            return {
              ...prev,
              ...flyerItem,
              name: flyerItem?.name || TextResult?.english,
              nameChinese: flyerItem?.nameChinese || TextResult?.chinese,
              gfMultiImages: [...multiImages],
              gfFlyerItemCategory:
                prev?.gfFlyerItemCategory || flyerItem?.gfFlyerItemCategory,
            };
          });
        } else {
          const multiImages = images?.map((gfImage) => ({
            gfFlyerItem: {} as GfFlyerItem,
            gfImage,
            id: gfImage.id,
          })) as GfMultiImage[];
          setUpdateFlyerItem((prev) => {
            return {
              ...prev,
              gfMultiImages: [...multiImages],
            };
          });
        }
      }
    },
    initialPickedImages: initialPickedImages,
    enableDuplicateImageSelection: true,
    storeIds:
      updateFlyerItem?.flyerItemList?.stores?.map((store) => store.id) || [],
    dragAndDrop: { enabled: true, defaultPath: "productImages" },
  });

  const handleSwitchChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    setIsAvailableNow(checked);
    if (checked) {
      setUpdateFlyerItem((prevValue) => ({
        ...prevValue,
        salePrice: 0,
      }));
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleOnImageFileSelect(e, "productImages");
    handleOnImageSearchModalClose();
  };
  /**
   * Handles background removal for images
   * Filters out images that already have background removed (originalSrc exists)
   * and only processes images that haven't been processed yet
   * Now uses queue-based processing with polling
   */
  const appendAiImprovedImage = (newImage: GfImage) => {
    const newMultiImage = {
      gfFlyerItem: {} as GfFlyerItem,
      gfImage: newImage,
      id: newImage.id,
    } as GfMultiImage;

    setUpdateFlyerItem((prev) => {
      const existing = prev?.gfMultiImages ?? [];
      const alreadyAdded = existing.some((mi) => mi.gfImage.id === newImage.id);
      const gfMultiImages = alreadyAdded
        ? existing
        : [...existing, newMultiImage];
      return { ...prev, gfMultiImages };
    });

    setPickedGfImages((prev) => {
      if (prev?.some((img) => img.id === newImage.id)) return prev;
      return [...(prev ?? []), newImage];
    });
  };

  const handleStartAiImprovement = async (prompt: string, imageId: string) => {
    if (!imageId) {
      toastError(t(messages.noImagesSelected()));
      return;
    }

    setIsStartingAiImprove(true);
    try {
      const queueResponse =
        await adminApiService.gfImageAdminControllerAiImproveAndSaveAsNewImage(
          {
            imageId,
            prompt: prompt || undefined,
          },
          "productImages",
          authHeaders,
        );

      if (!queueResponse?.jobId) {
        toastError(t(messages.failedToAiImprove()));
        return;
      }

      if (!queueResponse.isNew) {
        toastSuccess(t(messages.aiImprovementAlreadyRunning()));
      }

      handledAiImproveJobRef.current = null;
      saveAiImprovePendingJob(queueResponse.jobId, imageId);
      setAiImproveSourceImageId(imageId);
      setAiImproveJobId(queueResponse.jobId);
      setIsImprovingWithAi(true);
      setAiImproveModalOpen(false);
    } catch (error) {
      console.error("AI image improvement error:", error);
      toastError(t(messages.failedToAiImprove()));
      setIsImprovingWithAi(false);
    } finally {
      setIsStartingAiImprove(false);
    }
  };

  /**
   * Queues background-removal jobs for the given images and polls until done.
   */
  const processBackgroundRemoval = async (
    imagesToProcess: GfMultiImage[],
  ) => {
    if (!imagesToProcess.length) {
      return;
    }

    // Clear any existing polling timeout
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }

    // Ids of the images being (re)processed, so the new background-removed
    // versions replace—rather than duplicate—them when the jobs complete.
    const processedImageIds = new Set(
      imagesToProcess.map((multiImage) => multiImage.gfImage.id),
    );

    try {
      setIsRemovingBackground(true);

      const currentImageUrls = imagesToProcess.map((val) => val.gfImage);

      // Queue the background removal jobs
      const queueResponse =
        await adminApiService.gfImageAdminControllerRemoveBackgroundAndSaveAsNewImages(
          currentImageUrls,
          "productImages",
          authHeaders,
        );

      if (!queueResponse?.jobs?.length) {
        toastError(t(messages.failedToRemoveBackground()));
        setIsRemovingBackground(false);
        return;
      }

      console.log("Background removal jobs queued:", queueResponse.jobs);

      // Start polling for job completion
      const jobIds = queueResponse.jobs.map((job) => job.jobId);
      const pollingInterval = 2000; // Poll every 2 seconds
      const maxPollingTime = 300000; // Maximum 5 minutes
      const startTime = Date.now();

      const pollJobStatus = async (): Promise<void> => {
        // Check if component is still mounted
        if (!isMountedRef.current) {
          console.log("Component unmounted, stopping polling");
          return;
        }

        console.log("Polling job status...");

        // Check if max polling time exceeded
        if (Date.now() - startTime > maxPollingTime) {
          console.error("Polling timeout exceeded");
          if (isMountedRef.current) {
            toastError(t(messages.failedToRemoveBackground()) + " (Timeout)");
            setIsRemovingBackground(false);
          }
          return;
        }

        try {
          // Check status of all jobs
          const statusPromises = jobIds.map((jobId) =>
            adminApiService.gfImageAdminControllerGetBackgroundRemovalJobStatus(
              jobId,
              authHeaders,
            ),
          );
          const statuses = await Promise.all(statusPromises);

          console.log(
            "Job statuses:",
            statuses.map((s) => ({ id: s.id, state: s.state })),
          );

          // Check if all jobs are completed or failed
          const completedJobs = statuses.filter(
            (status) =>
              status.state ===
              BackgroundRemovalJobStatusDto.StateEnum.Completed,
          );
          const failedJobs = statuses.filter(
            (status) =>
              status.state === BackgroundRemovalJobStatusDto.StateEnum.Failed,
          );
          const activeOrWaitingJobs = statuses.filter(
            (status) =>
              status.state === BackgroundRemovalJobStatusDto.StateEnum.Active ||
              status.state ===
                BackgroundRemovalJobStatusDto.StateEnum.Waiting ||
              status.state === BackgroundRemovalJobStatusDto.StateEnum.Delayed,
          );

          console.log(
            `Jobs - Completed: ${completedJobs.length}, Failed: ${failedJobs.length}, Active/Waiting: ${activeOrWaitingJobs.length}`,
          );

          // If there are still active/waiting jobs, continue polling
          if (activeOrWaitingJobs.length > 0) {
            console.log(`Continuing polling in ${pollingInterval}ms...`);
            if (isMountedRef.current) {
              pollingTimeoutRef.current = setTimeout(() => {
                pollJobStatus();
              }, pollingInterval);
            }
            return;
          }

          // All jobs are either completed or failed
          if (completedJobs.length > 0) {
            // Extract the new images from completed jobs
            const newImages = completedJobs
              .map((job) => job.result?.newImage)
              .filter((img) => img !== undefined) as GfImage[];

            console.log("New images created:", newImages.length);

            if (newImages.length > 0 && isMountedRef.current) {
              const existingBgRemovedImages =
                updateFlyerItem?.gfMultiImages?.filter(
                  (multiImage) =>
                    multiImage.gfImage.originalSrc &&
                    !processedImageIds.has(multiImage.gfImage.id),
                ) || [];

              const newBackgroundRemovedImages = newImages.map((val) => ({
                gfFlyerItem: {} as GfFlyerItem,
                gfImage: val,
                id: val.id,
              })) as GfMultiImage[];

              const allMultiImages = [
                ...existingBgRemovedImages,
                ...newBackgroundRemovedImages,
              ];

              setUpdateFlyerItem((prev) => ({
                ...prev,
                gfMultiImages: allMultiImages,
              }));

              const allGfImages = allMultiImages.map(
                (multiImage) => multiImage.gfImage,
              );
              setPickedGfImages(allGfImages);

              if (failedJobs.length > 0) {
                toastSuccess(
                  `${t(messages.backgroundRemovedSuccess())} (${completedJobs.length}/${statuses.length} succeeded)`,
                );
              } else {
                toastSuccess(t(messages.backgroundRemovedSuccess()));
              }
            }
          }

          if (failedJobs.length > 0 && completedJobs.length === 0) {
            // All jobs failed
            const errorMessages = failedJobs
              .map((job) => job.error)
              .filter(Boolean)
              .join(", ");
            console.error("All jobs failed:", errorMessages);
            if (isMountedRef.current) {
              toastError(
                `${t(messages.failedToRemoveBackground())}${errorMessages ? ": " + errorMessages : ""}`,
              );
            }
          }

          if (isMountedRef.current) {
            setIsRemovingBackground(false);
          }
          pollingTimeoutRef.current = null;
        } catch (error) {
          console.error("Error polling job status:", error);
          if (isMountedRef.current) {
            toastError(t(messages.failedToRemoveBackground()));
            setIsRemovingBackground(false);
          }
          pollingTimeoutRef.current = null;
        }
      };

      // Start polling
      console.log("Starting background removal polling...");
      pollJobStatus();
    } catch (error) {
      console.error("Background removal error:", error);
      toastError(t(messages.failedToRemoveBackground()));
      setIsRemovingBackground(false);
    }
  };

  /**
   * Entry point for the "Remove Background" button.
   * - Processes images that haven't been background-removed yet.
   * - Also (re)processes AI-improved images, whose src contains "ai_improved".
   * - If every selected image already looks processed, asks the user whether
   *   to remove the background again instead of silently erroring out.
   */
  const handleRemoveBackground = async () => {
    if (!updateFlyerItem?.gfMultiImages?.length) {
      toastError(t(messages.noImagesSelected()));
      return;
    }

    const imagesToProcess = updateFlyerItem.gfMultiImages.filter(
      (multiImage) =>
        !multiImage.gfImage.originalSrc ||
        multiImage.gfImage.src?.includes("ai_improved"),
    );

    if (imagesToProcess.length === 0) {
      // Nothing obviously needs processing — confirm before forcing a re-run.
      setShowRemoveBgConfirm(true);
      return;
    }

    await processBackgroundRemoval(imagesToProcess);
  };

  /**
   * User confirmed re-removing the background even though the selected images
   * already appear to have had it removed.
   */
  const handleConfirmRemoveBackgroundAgain = async () => {
    setShowRemoveBgConfirm(false);
    if (!updateFlyerItem?.gfMultiImages?.length) {
      return;
    }
    await processBackgroundRemoval(updateFlyerItem.gfMultiImages);
  };

  /**
   * add a new unit, then selected unit will be the new  unit
   * @param value
   */
  const handleAddNewUnit = async (value?: GfUnit) => {
    if (value?.name) {
      const unitV2 =
        await adminApiService.createOneBaseGfUnitAdminControllerGfUnit({
          name: value.name,
          adminUser: state.gfAdminUser,
        });
      setUpdateFlyerItem((prevValue) => ({
        ...prevValue,
        unit: unitV2.name,
        unitV2: unitV2,
      }));

      fetchUnits();
    }
  };

  /**
   * Handle opening the image editor
   */
  const handleOpenImageEditor = (image: GfImage | undefined) => {
    if (!image?.src) {
      return;
    }
    setEditingImage(image);
    setEditingImageSrc(image.src);
    setImageEditorOpen(true);
  };

  /**
   * Handle closing the image editor
   */
  const handleCloseImageEditor = () => {
    setImageEditorOpen(false);
    setEditingImageSrc("");
    setEditingImage(null);
  };

  /**
   * Handle saving edited image from the editor
   */
  const handleSaveEditedImage = async (
    editedImageObject: savedImageData,
    _designState: imageDesignState,
    currentImage: GfImage | null | undefined,
  ) => {
    try {
      showLoadingScreen(true);
      setSavingEditedImage(true);

      const base64Data = editedImageObject.imageBase64;
      if (!base64Data) {
        toastError("Missing base64 image data");
        return;
      }
      const blob = await fetch(base64Data).then((res) => res.blob());

      const originalImage = currentImage ?? editingImage;

      if (!originalImage?.src) {
        throw new Error("Missing original image source for overwrite");
      }

      if (!originalImage.id) {
        throw new Error("Missing original image id for overwrite");
      }

      const fileType = blob.type || editedImageObject?.mimeType || "image/png";

      const fileName =
        originalImage.fileName ||
        editedImageObject?.fullName ||
        "edited-image.png";

      const editedFile = new File([blob], fileName, { type: fileType });

      const rawAuthToken = authHeaders?.headers?.Authorization;
      const accessToken = rawAuthToken?.startsWith("Bearer ")
        ? rawAuthToken.substring(7)
        : rawAuthToken;

      await replaceImageInS3Only(originalImage.id, editedFile, accessToken);

      const timestamp = Date.now();
      const cacheBustedSrc = `${originalImage.src}${
        originalImage.src.includes("?") ? "&" : "?"
      }_t=${timestamp}`;
      const cacheBustedSrcSmall = originalImage.srcSmall
        ? `${originalImage.srcSmall}${
            originalImage.srcSmall.includes("?") ? "&" : "?"
          }_t=${timestamp}`
        : undefined;

      setUpdateFlyerItem((prev) => {
        if (!prev?.gfMultiImages?.length) {
          return prev;
        }

        const updatedMultiImages = prev.gfMultiImages.map((multiImage) =>
          multiImage.gfImage.id === originalImage.id
            ? {
                ...multiImage,
                gfImage: {
                  ...multiImage.gfImage,
                  src: cacheBustedSrc,
                  srcSmall: cacheBustedSrcSmall,
                },
              }
            : multiImage,
        );

        return {
          ...prev,
          gfMultiImages: updatedMultiImages,
        };
      });

      setPickedGfImages((prevImages) => {
        if (!prevImages?.length) {
          return prevImages;
        }

        return prevImages.map((img) =>
          img.id === originalImage.id
            ? { ...img, src: cacheBustedSrc, srcSmall: cacheBustedSrcSmall }
            : img,
        );
      });

      toastSuccess("Image edited successfully");
    } catch (error) {
      console.error("Error saving edited image:", error);
      toastError("Failed to save edited image");
    } finally {
      showLoadingScreen(false);
      handleCloseImageEditor();

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        setSavingEditedImage(false);
        saveTimeoutRef.current = null;
      }, 8000);
    }
  };

  const handleTranslateEnglishToFrench = async () => {
    const nameFrench =
      await adminApiService.gfMiscellaneousAdminControllerTranslateTextByGoogle(
        {
          sourceLang: "en",
          targetLang: "fr",
          text: updateFlyerItem?.name ?? "",
        },
      );
    setUpdateFlyerItem((prevValue) => {
      if (!prevValue) {
        return prevValue;
      }

      return {
        ...prevValue,
        nameFrench: nameFrench.result ?? "",
      };
    });
  };

  const handleImageTagSaved = (updatedImage: GfImage) => {
    setUpdateFlyerItem((prev) => {
      if (!prev?.gfMultiImages) return prev;
      return {
        ...prev,
        gfMultiImages: prev.gfMultiImages.map((mi) =>
          mi.gfImage.id === updatedImage.id
            ? {
                ...mi,
                gfImage: {
                  ...mi.gfImage,
                  imageTags: updatedImage.imageTags,
                },
              }
            : mi,
        ),
      };
    });
    setProductImageList((prev) =>
      prev.map((img) =>
        img.id === updatedImage.id
          ? { ...img, imageTags: updatedImage.imageTags }
          : img,
      ),
    );
    onImageTagSaved?.(updatedImage);
    setImageForTagEdit(null);
  };
  useEffect(() => {
    if (flyerItemCategories.length && updateFlyerItem?.gfMultiImages?.length) {
      const noExistingSelection =
        !updateFlyerItem?.id &&
        !state?.user?.gfFlyerItemCategory?.length &&
        !updateFlyerItem?.gfFlyerItemCategory?.length;

      // Only auto-select once per item to allow manual clearing
      const shouldAutoSelect =
        noExistingSelection && !hasAutoSelectedRef.current;

      console.log(
        "category auto-select check",
        updateFlyerItem?.gfFlyerItemCategory?.length,
        "shouldAutoSelect:",
        shouldAutoSelect,
      );

      if (shouldAutoSelect) {
        const categoriesFromUrl = detectCategoriesFromURL(
          updateFlyerItem?.gfMultiImages[0].gfImage.src,
          flyerItemCategories,
        );
        console.log(
          "category auto-select  categoriesFromUrl",
          categoriesFromUrl,
        );

        if (categoriesFromUrl?.length) {
          setUpdateFlyerItem((prev) => ({
            ...prev,
            gfFlyerItemCategory: categoriesFromUrl,
          }));
          hasAutoSelectedRef.current = true;
        } else if (lastDepartmentIds?.length) {
          // Fallback to last selected departments for this flyerItemListId
          const mapped = flyerItemCategories.filter((c) =>
            lastDepartmentIds.includes(String(c?.id)),
          );

          console.log("category auto-select  last-selected", mapped);
          if (mapped.length) {
            setUpdateFlyerItem((prev) => ({
              ...prev,
              gfFlyerItemCategory: mapped,
            }));
            hasAutoSelectedRef.current = true;
          }
        }
      }
    }

    // Reset flag when switching to a different item
    if (updateFlyerItem?.id) {
      hasAutoSelectedRef.current = false;
    }
  }, [
    flyerItemCategories,
    updateFlyerItem?.gfMultiImages,
    updateFlyerItem?.id,
    state?.user?.gfFlyerItemCategory?.length,
    lastDepartmentIds,
  ]);

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;

      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, []);

  const clearAiImproveActiveState = (sourceImageId?: string | null) => {
    if (sourceImageId) {
      clearAiImprovePendingJob(sourceImageId);
    } else if (aiImproveSourceImageId) {
      clearAiImprovePendingJob(aiImproveSourceImageId);
    }
    setAiImproveJobId(null);
    setIsImprovingWithAi(false);
    setAiImproveSourceImageId(null);
  };

  useEffect(() => {
    if (!aiImproveJobId || !isMountedRef.current) {
      return;
    }

    if (aiImproveJobNotFound) {
      if (handledAiImproveJobRef.current !== aiImproveJobId) {
        handledAiImproveJobRef.current = aiImproveJobId;
        clearAiImprovePendingJobByJobId(aiImproveJobId);
        toastWarn(t(messages.aiImprovementSessionExpired()));
      }
      clearAiImproveActiveState(aiImproveSourceImageId);
      return;
    }

    if (aiImprovePollError) {
      if (handledAiImproveJobRef.current !== aiImproveJobId) {
        handledAiImproveJobRef.current = aiImproveJobId;
        toastError(t(messages.failedToAiImprove()));
      }
      clearAiImprovePendingJobByJobId(aiImproveJobId);
      clearAiImproveActiveState(aiImproveSourceImageId);
      return;
    }

    if (!isAiImproveDone) {
      return;
    }
    if (handledAiImproveJobRef.current === aiImproveJobId) {
      return;
    }
    handledAiImproveJobRef.current = aiImproveJobId;

    if (isAiImproveSuccess && aiImprovedNewImage) {
      appendAiImprovedImage(aiImprovedNewImage);
      toastSuccess(t(messages.aiImprovementSuccess()));
    } else if (isAiImproveFailed) {
      const errorMessage =
        aiImproveStatus?.error ?? t(messages.failedToAiImprove());
      toastError(errorMessage);
    }

    clearAiImprovePendingJobByJobId(aiImproveJobId);
    clearAiImproveActiveState(aiImproveSourceImageId);
  }, [
    aiImproveJobId,
    isAiImproveDone,
    isAiImproveSuccess,
    isAiImproveFailed,
    aiImprovedNewImage,
    aiImproveStatus?.error,
    aiImprovePollError,
    aiImproveJobNotFound,
    aiImproveSourceImageId,
  ]);

  useEffect(() => {
    if (
      aiImproveJobId ||
      isImprovingWithAi ||
      !aiImproveCandidateImages.length ||
      hasAttemptedAiRecoverRef.current
    ) {
      return;
    }

    hasAttemptedAiRecoverRef.current = true;
    let cancelled = false;

    const recoverFromSession = async () => {
      const imageIds = aiImproveCandidateImages.map((img) => img.id!);
      const pending = getAiImprovePendingJobsForImages(imageIds);
      if (!pending.length) return;

      for (const entry of pending) {
        if (cancelled) return;

        const result = await fetchAiImprovementJobStatusSafe(
          entry.jobId,
          authHeaders,
        );

        if (cancelled) return;

        if (result.type === "not_found") {
          clearAiImprovePendingJob(entry.sourceImageId);
          continue;
        }

        if (result.type === "error") {
          continue;
        }

        const { job } = result;
        if (!isAiImprovementTerminal(job.state)) {
          handledAiImproveJobRef.current = null;
          setAiImproveSourceImageId(entry.sourceImageId);
          setAiImproveJobId(entry.jobId);
          setIsImprovingWithAi(true);
          return;
        }

        clearAiImprovePendingJob(entry.sourceImageId);
      }
    };

    recoverFromSession();
    return () => {
      cancelled = true;
    };
  }, [aiImproveCandidateImages, aiImproveJobId, isImprovingWithAi]);

  useEffect(() => {
    hasAttemptedAiRecoverRef.current = false;
  }, [updateFlyerItem?.id]);

  useEffect(() => {
    //if the logged in user is managing only one store, auto select that store
    //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) I think we should create a common component for store selection. and use this logic in that common component
    if (state.user && state.user.stores && state.user.stores.length === 1) {
      setUpdateFlyerItem((prev) => ({
        ...prev,
        stores: state.user?.stores,
      }));
    }

    //if the logged in user is managing only one department, auto select that department
    //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) I think we should create a common component for department selection. and use this logic in that common component
    if (
      state.user &&
      state.user.gfFlyerItemCategory &&
      state.user.gfFlyerItemCategory.length === 1 &&
      !updateFlyerItem?.gfFlyerItemCategory?.length
    ) {
      setUpdateFlyerItem((prev) => ({
        ...prev,
        gfFlyerItemCategory: state.user?.gfFlyerItemCategory,
      }));
    }
  }, []);

  return (
    <div data-testid="createflyermodal-test-id">
      <CardItem>
        {" "}
        <LeftSideWrapper aria-label="Flyer images drop zone">
          <ImageDropZone
            uploader={{
              getDropZoneProps,
              isDragging,
              showReorderModal,
              pendingDropFiles,
              handleReorderConfirm,
              handleReorderCancel,
            }}
            className="flyer-image-drop-zone"
            label={t(messages.Drag_your_image_here())}
            overlayContent={t(messages.Drag_your_image_here())}
            showOverlay
            overlayStyles={{}}
            style={{
              position: "relative",
              ...(isDragging
                ? {
                    outline: "2px dashed #ff813a",
                    background: "#fff7f2",
                  }
                : {}),
            }}
          >
            {!savingEditedImage ? (
              <>
                {!isUploadingImage &&
                  !isRemovingBackground &&
                  !isImprovingWithAi &&
                  updateFlyerItem?.gfMultiImages &&
                  (updateFlyerItem?.gfMultiImages?.length === 1 ? (
                    <>
                      <ImageCardWrapper>
                        <ImageCard
                          data-testid={`singleUploadedImage`}
                          src={updateFlyerItem?.gfMultiImages[0]?.gfImage.src}
                          alt={updateFlyerItem?.gfMultiImages[0]?.gfImage?.alt}
                          key={updateFlyerItem?.gfMultiImages[0]?.gfImage.id}
                        ></ImageCard>
                        <PermissionComponentWrapper
                          requiredPermission={["update"]}
                          requiredPermissionGroup={["imageTag"]}
                          wrapperCss={`
                          display:block;
                          margin-bottom:5px;
                          margin-top: 5px;
                          `}
                        >
                          <ImageTagButton>
                            <IconButton
                              sx={{
                                color: "white",
                                backgroundColor: "#f58220",
                                "&:hover": {
                                  backgroundColor: "#d96e10",
                                },
                              }}
                              onClick={() =>
                                setImageForTagEdit(
                                  updateFlyerItem?.gfMultiImages?.[0]
                                    ?.gfImage ?? null,
                                )
                              }
                              data-testid={`singleUploadedImage-tag`}
                            >
                              <LocalOfferIcon />
                            </IconButton>
                          </ImageTagButton>
                        </PermissionComponentWrapper>
                        <ImageEditButton>
                          <IconButton
                            sx={{
                              color: "white",
                              backgroundColor: "#ff813a",
                              "&:hover": {
                                backgroundColor: "#ff6520",
                              },
                            }}
                            onClick={() =>
                              handleOpenImageEditor(
                                updateFlyerItem?.gfMultiImages?.[0]?.gfImage,
                              )
                            }
                            data-testid={`singleUploadedImage-edit`}
                          >
                            <EditIcon />
                          </IconButton>
                        </ImageEditButton>
                      </ImageCardWrapper>
                    </>
                  ) : (
                    <MultipleImages>
                      {updateFlyerItem?.gfMultiImages?.map((val) => {
                        const isAiImproveTarget =
                          isImprovingWithAi &&
                          aiImproveSourceImageId === val.gfImage.id;
                        return (
                          <SmallImageWrapper
                            key={val.id}
                            data-testid="createflyermodal-test-id"
                            style={
                              isAiImproveTarget
                                ? {
                                    outline: "2px solid #f58220",
                                    borderRadius: 4,
                                  }
                                : undefined
                            }
                          >
                            <SmallDealImgCard
                              imageSrc={val.gfImage.src}
                              cancelButtonClick={() => {
                                handleOnRemoveImage(val.gfImage.id);
                              }}
                              editButtonClick={() =>
                                handleOpenImageEditor(val.gfImage)
                              }
                              tagButtonClick={() =>
                                setImageForTagEdit(val.gfImage)
                              }
                            />
                          </SmallImageWrapper>
                        );
                      })}
                    </MultipleImages>
                  ))}
              </>
            ) : (
              <ImageLoaderWrapper
                data-testid={"flyer-item-image-saving-loader"}
              >
                <div style={{ textAlign: "center" }}>
                  <CircularProgress
                    sx={{ color: "#ff813a" }}
                    size={100}
                    style={{ margin: "auto" }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#ff813a",
                      mt: 2,
                      fontWeight: 500,
                    }}
                  >
                    {t(messages.Loading())}
                  </Typography>
                </div>
              </ImageLoaderWrapper>
            )}
            {(isUploadingImage ||
              isRemovingBackground ||
              isImprovingWithAi) && (
              <ImageLoaderWrapper
                data-testid={"flyer-item-image-uploading-loader"}
              >
                <div style={{ textAlign: "center" }}>
                  <CircularProgress
                    sx={{ color: "#ff813a" }}
                    size={100}
                    style={{ margin: "auto" }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#ff813a",
                      mt: 2,
                      fontWeight: 500,
                    }}
                  >
                    {isUploadingImage
                      ? t(messages.uploadImages())
                      : isImprovingWithAi
                        ? t(messages.improvingWithAi(), {
                            progress: aiImproveProgress,
                          })
                        : t(messages.removingBackground())}
                  </Typography>
                </div>
              </ImageLoaderWrapper>
            )}
            {!isRemovingBackground && !isImprovingWithAi && (
              <ImageSubTitle data-testid={`singleUploadedImageAltText`}>
                {updateFlyerItem?.gfMultiImages?.[0]?.gfImage?.alt &&
                  updateFlyerItem?.gfMultiImages?.[0]?.gfImage.alt}{" "}
              </ImageSubTitle>
            )}
            {(updateFlyerItem?.gfMultiImages?.length === 0 ||
              updateFlyerItem?.gfMultiImages?.length === undefined ||
              updateFlyerItem?.gfMultiImages?.length === null) &&
              !isUploadingImage &&
              !isRemovingBackground &&
              !isImprovingWithAi && <ImageCardEmpty></ImageCardEmpty>}
            <ButtonWrapper>
              <ButtonWithTooltip
                disabled={
                  isRemovingBackground || isUploadingImage || isImprovingWithAi
                }
                onClick={
                  isRemovingBackground || isUploadingImage || isImprovingWithAi
                    ? undefined
                    : handleOnImageSearchModalOpen
                }
                data-testid="change-image"
                showTooltip
                tooltipText={t(messages.TWO_IMAGE_LAYOUT_GUIDE())}
                tooltipPlacement="top"
                variant="outline"
                disableOnTouch={false}
                size="small"
                customStyles={imageActionButtonStyles}
              >
                {t(messages.Browse_Image())}
              </ButtonWithTooltip>
              <input
                type="file"
                id="upload"
                accept="image/*,.psd"
                multiple={true}
                hidden
                disabled={
                  isRemovingBackground || isUploadingImage || isImprovingWithAi
                }
                ref={fileInputRef}
                onChange={(e) => handleOnImageFileSelect(e, "productImages")}
                data-testid="local-image"
              />

              <ButtonWithTooltip
                disabled={
                  isRemovingBackground || isUploadingImage || isImprovingWithAi
                }
                onClick={() => fileInputRef.current?.click()}
                data-testid="local-image"
                showTooltip
                tooltipText={t(messages.UPLOAD_IMAGES_GUIDE())}
                tooltipPlacement="top"
                variant="outline"
                disableOnTouch={false}
                size="small"
                customStyles={imageActionButtonStyles}
              >
                {t(messages.Local_Upload())}
              </ButtonWithTooltip>
            </ButtonWrapper>
            <UrlImagePasteUpload
              handleOnImageFileSelect={handleOnImageFileSelect}
              busy={
                isRemovingBackground || isUploadingImage || isImprovingWithAi
              }
              zIndex={1500}
              compact
            />
            <ButtonWithTooltip
              disabled={
                isRemovingBackground ||
                isUploadingImage ||
                isImprovingWithAi ||
                !updateFlyerItem?.gfMultiImages?.[0]?.gfImage
              }
              onClick={
                isRemovingBackground ||
                isImprovingWithAi ||
                !updateFlyerItem?.gfMultiImages?.[0]?.gfImage
                  ? undefined
                  : handleRemoveBackground
              }
              data-testid="remove-background"
              showTooltip
              tooltipText={t(messages.REMOVE_BACKGROUND_GUIDE())}
              tooltipPlacement="bottom"
              variant="outline"
              size="small"
              disableOnTouch={false}
              customStyles={{
                ...imageActionButtonStyles,
                marginTop: "4px",
              }}
            >
              {isRemovingBackground
                ? t(messages.processing())
                : t(messages.removeBackground())}
            </ButtonWithTooltip>
            <ButtonWithTooltip
              disabled={
                isRemovingBackground ||
                isUploadingImage ||
                isImprovingWithAi ||
                !hasAiImproveCandidateImages
              }
              onClick={
                isImprovingWithAi || !hasAiImproveCandidateImages
                  ? undefined
                  : () => setAiImproveModalOpen(true)
              }
              data-testid="improve-with-ai"
              showTooltip
              tooltipText={t(messages.IMPROVE_WITH_AI_GUIDE())}
              tooltipPlacement="bottom"
              variant="outline"
              size="small"
              disableOnTouch={false}
              customStyles={{
                ...imageActionButtonStyles,
                marginTop: "4px",
              }}
            >
              {isImprovingWithAi
                ? t(messages.improvingWithAi(), {
                    progress: aiImproveProgress,
                  })
                : t(messages.improveWithAi())}
            </ButtonWithTooltip>
          </ImageDropZone>
        </LeftSideWrapper>
        <RightSideWrapper>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="caption" fontSize={16}>
              {t(messages.English_Name())} *
            </Typography>
            <Tooltip
              enterDelay={1000}
              title={t(messages.English_Name_Tooltip())}
              arrow
            >
              <InfoIcon
                sx={{ fontSize: 15, color: "#9ca3afab", cursor: "help" }}
              />
            </Tooltip>
          </Stack>
          <AutoResizeTextField
            placeholder={`${t(messages.English_Name())} *`}
            onChange={(event) => {
              setUpdateFlyerItem((prevValue) => ({
                ...prevValue,
                name: event.target.value,
              }));
            }}
            value={updateFlyerItem?.name}
            data-testid="first-name-input"
          ></AutoResizeTextField>

          <Stack direction="row" alignItems="center" spacing={1} mt={1}>
            <Typography variant="caption" fontSize={16}>
              {t(messages.Chinese_Name())}
            </Typography>
            <Tooltip
              enterDelay={1000}
              title={t(messages.Chinese_Name_Tooltip())}
              arrow
            >
              <InfoIcon
                sx={{ fontSize: 15, color: "#9ca3afab", cursor: "help" }}
              />
            </Tooltip>
          </Stack>
          <AutoResizeTextField
            placeholder={`${t(messages.Chinese_Name())}`}
            onChange={(event) => {
              setUpdateFlyerItem((prevValue) => ({
                ...prevValue,
                nameChinese: event.target.value,
              }));
            }}
            value={updateFlyerItem?.nameChinese}
            data-testid="second-name-input"
          ></AutoResizeTextField>

          {updateFlyerItem?.flyerItemList?.stores &&
            getMontrealStores(updateFlyerItem.flyerItemList.stores).length >
              0 && (
              <InputWithButton
                placeholder={`${t(messages.French())}`}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setUpdateFlyerItem((prevValue) => ({
                    ...prevValue,
                    nameFrench: event.target.value,
                  }));
                }}
                value={updateFlyerItem?.nameFrench}
                data-testid="third-name-input"
                onButtonClick={handleTranslateEnglishToFrench}
                buttonLabel={t(messages.Translate())}
                buttonTooltip={t(messages.Translate())}
                buttonDisabled={!updateFlyerItem?.name}
                buttonDataTestId="french-translate-button"
                showButton={updateFlyerItem?.name != null}
              />
            )}
          <DotDotDivider></DotDotDivider>

          <SalesSectionWrapper>
            <PriceWrapper>
              <RegularPrice>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption" fontSize={16}>
                    {t(messages.Quantity())}
                  </Typography>
                  <Tooltip
                    enterDelay={1000}
                    title={t(messages.Quantity_Tooltip())}
                    arrow
                  >
                    <InfoIcon
                      sx={{ fontSize: 15, color: "#9ca3afab", cursor: "help" }}
                    />
                  </Tooltip>
                </Stack>
                <InheritTextInputFieldV2
                  placeholder={`${t(messages.Quantity())}`}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUpdateFlyerItem((prevValue) => ({
                      ...prevValue,
                      quantity: parseFloat(event.target.value),
                    }));
                  }}
                  value={updateFlyerItem?.quantity}
                  type="number"
                  data-testid="quantity-input"
                  onWheel={disableWheelOnInput}
                ></InheritTextInputFieldV2>
              </RegularPrice>
              <RegularPrice>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption" fontSize={16} noWrap>
                    {t(messages.Sale_Price())}
                  </Typography>
                  <Tooltip
                    enterDelay={1000}
                    title={t(messages.Sale_Price_Tooltip())}
                    arrow
                  >
                    <InfoIcon
                      sx={{ fontSize: 15, color: "#9ca3afab", cursor: "help" }}
                    />
                  </Tooltip>
                </Stack>
                <InheritTextInputFieldV2
                  placeholder={
                    isAvailableNow
                      ? t(messages.Available_Now())
                      : t(messages.Sale_Price())
                  }
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const newValue =
                      parseFloat(event.target.value) || undefined;
                    setUpdateFlyerItem((prevValue) => ({
                      ...prevValue,
                      salePrice: newValue,
                    }));
                  }}
                  value={isAvailableNow ? "" : updateFlyerItem?.salePrice}
                  data-testid={"sale-price-input"}
                  onWheel={disableWheelOnInput}
                  type="number"
                  disabled={isAvailableNow}
                  style={{
                    backgroundColor: isAvailableNow ? "#f3f4f6" : "white",
                    color: isAvailableNow ? "#6b7280" : "inherit",
                  }}
                />
              </RegularPrice>
              <RegularPrice>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="caption" fontSize={16} noWrap>
                    {t(messages.Regular_Price())}
                  </Typography>
                  <Tooltip
                    enterDelay={1000}
                    title={t(messages.Regular_Price_Tooltip())}
                    arrow
                  >
                    <InfoIcon
                      sx={{ fontSize: 15, color: "#9ca3afab", cursor: "help" }}
                    />
                  </Tooltip>
                </Stack>
                <InheritTextInputFieldV2
                  placeholder={`${t(messages.Regular_Price())}`}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setUpdateFlyerItem((prevValue) => ({
                      ...prevValue,
                      regularPriceString: event.target.value,
                    }));
                  }}
                  value={updateFlyerItem?.regularPriceString}
                  data-testid="regular-price-input"
                  type={"text"}
                ></InheritTextInputFieldV2>
              </RegularPrice>
            </PriceWrapper>
            <RankPriceRemarksWrapperV2>
              <InheritTextInputField
                placeholder={`+ ${t(messages.Price_Remarks())}`}
                data-testid="price-remarks-input"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  //TODO: (if you touch code here, please refactor. if you don't know the issue, please talk to me) need to find out the type here
                  setUpdateFlyerItem((prevValue) => ({
                    ...prevValue,
                    priceRemarks: event.target.value,
                  }));
                }}
                value={updateFlyerItem?.priceRemarks}
              ></InheritTextInputField>{" "}
              <Tooltip
                enterDelay={1000}
                title={t(messages.Sale_Price_Zero_Tooltip()) as string}
                placement="top"
                arrow
              >
                <SwitchWrapper>
                  <Switch
                    checked={isAvailableNow}
                    onChange={handleSwitchChange}
                    size="small"
                    color="warning"
                    data-testid="available-now-switch"
                  />
                  <Typography variant="body2" sx={{ fontSize: "12px" }}>
                    {t(messages.Available_Now())}
                  </Typography>
                </SwitchWrapper>
              </Tooltip>
            </RankPriceRemarksWrapperV2>
          </SalesSectionWrapper>
          <DotDotDivider></DotDotDivider>
          <UnitWrapper>
            <SelectSearchWithAddMui<GfUnit>
              title={`${t(messages.Unit())} *`}
              toolTipInfoText={t(messages.Unit_Tooltip())}
              selectOptions={unitsList}
              selectedValue={
                updateFlyerItem?.unitV2 ||
                unitsList?.find((val) => val.name === updateFlyerItem?.unit)
              }
              onSelect={(value) => {
                setUpdateFlyerItem((prevValue) => ({
                  ...prevValue,
                  unit: value.name,
                  unitV2: value,
                }));
              }}
              onAddNew={handleAddNewUnit}
              dataTestId="unit-select"
              popperZIndex={1500}
            ></SelectSearchWithAddMui>
            <LimitWrapper>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="caption" fontSize={16} noWrap>
                  {t(messages.Limit())}
                </Typography>
                <Tooltip
                  enterDelay={1000}
                  title={t(messages.Limit_Tooltip())}
                  arrow
                >
                  <InfoIcon
                    sx={{ fontSize: 15, color: "#9ca3afab", cursor: "help" }}
                  />
                </Tooltip>
              </Stack>
              <LimitInput
                placeholder={`${t(messages.Limit())}`}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setUpdateFlyerItem((prevValue) => ({
                    ...prevValue,
                    limitString: event.target.value,
                  }));
                }}
                value={updateFlyerItem?.limitString}
                type="string"
                data-testid="limit-select"
              ></LimitInput>
            </LimitWrapper>
          </UnitWrapper>
          <BottomSection>
            <BottomLeftWrapper>
              <RankPriceRemarksWrapper>
                <CustomAutocompleteMultiSelect
                  title={`${t(messages.Promotion_Tag())}`}
                  toolTipInfoText={t(messages.Promotion_Tag_Tooltip())}
                  optionsList={promotionTags?.map((tag) => ({
                    value: tag,
                    label: tag.name,
                  }))}
                  selectedValues={(updateFlyerItem?.promotionTag || [])?.map(
                    (tag) => ({
                      value: tag,
                      label: tag.name,
                    }),
                  )}
                  multiSelect={true}
                  handleOnChange={(value: GfPromotionTag[] | undefined) => {
                    setUpdateFlyerItem((prevValue) => ({
                      ...prevValue,
                      promotionTag: value,
                    }));
                  }}
                  WrapperCss={`
                  display:flex;
                  align-items: center;
                  justify-content: flex-start;
                  width: 100%;
                  column-gap:10px;
                  @media (max-width: 457px) {
                    justify-content: start;
                    flex-wrap:wrap;
                    row-gap:10px;
                  }
                  `}
                  titleCss={`width:80px; flex-shrink: 0;
                  `}
                  inputCss={`
                  flex: 1;
                  width: 100%;
                  max-width: 300px;
                  max-height: 5rem;
                  `}
                  dataTestId="promotionTag-select"
                />
                <RegularPrice>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="caption" fontSize={16} noWrap>
                      {t(messages.Rank())}
                    </Typography>
                    <Tooltip
                      enterDelay={1000}
                      title={t(messages.Rank_Tooltip())}
                      arrow
                    >
                      <InfoIcon
                        sx={{
                          fontSize: 15,
                          color: "#9ca3afab",
                          cursor: "help",
                        }}
                      />
                    </Tooltip>
                  </Stack>
                  <InheritTextInputFieldV2
                    placeholder={`${t(messages.Rank())}`}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setUpdateFlyerItem((prevValue) => ({
                        ...prevValue,
                        rank: parseInt(event.target.value),
                      }));
                    }}
                    value={updateFlyerItem?.rank}
                    data-testid="rank-input"
                    type="number"
                    onWheel={disableWheelOnInput}
                  ></InheritTextInputFieldV2>
                </RegularPrice>
              </RankPriceRemarksWrapper>

              <DotDotDivider></DotDotDivider>
              <CustomAutocompleteMultiSelect
                title={`${t(messages.Category())} *`}
                toolTipInfoText={t(messages.Category_Tooltip())}
                optionsList={flyerItemCategories.map((category) => ({
                  value: category,
                  label: category.name,
                }))}
                selectedValues={updateFlyerItem?.gfFlyerItemCategory?.map(
                  (category) => ({
                    value: category,
                    label: category.name,
                  }),
                )}
                multiSelect={true}
                handleOnChange={(value: GfFlyerItemCategory[] | undefined) => {
                  setUpdateFlyerItem((prevValue) => ({
                    ...prevValue,
                    gfFlyerItemCategory: value,
                  }));
                }}
                WrapperCss={`
                  display:flex;
                  align-items: center;
                  justify-content: space-around;
                  column-gap:10px;
                  @media (max-width: 457px) {
                    justify-content: start;
                    flex-wrap:wrap;
                    row-gap:10px;
                  }
                  `}
                inputCss={`
                  max-width:  18.75rem;
                  max-height: 5rem;
                  min-height: 68px;
                  `}
                titleCss={`width:80px;
                  text-wrap: nowrap;`}
                dataTestId="category-select"
              />
              {updateFlyerItem?.flyerItemList ? (
                <>
                  <DotDotDivider></DotDotDivider>
                  <Typography variant="subtitle1">
                    {t(messages.Store())}
                    {": "}
                    {makeStoreDisplayName(
                      updateFlyerItem.flyerItemList.stores[0],
                    )}
                  </Typography>
                </>
              ) : (
                <></>
              )}
            </BottomLeftWrapper>
          </BottomSection>
        </RightSideWrapper>
      </CardItem>
      <ImageSearchModal
        handleClose={handleOnImageSearchModalClose}
        open={openSearchImage}
        onSearch={handleOnSearchPressEnter}
        itemsList={productImageList}
        onItemClick={handleOnImageClick}
        hasMore={hasMoreGfImage}
        getMoreGfImage={handlePageChangeScroll}
        isSearchingImage={isSearchingImage}
        onFileUpload={handleImageUpload}
        onTagEdit={(image) => setImageForTagEdit(image)}
        enableKeywordSuggestions={true}
        zIndex={1500}
      />
      <ImageEditorModal
        open={imageEditorOpen}
        onClose={handleCloseImageEditor}
        imageSrc={editingImageSrc}
        onSave={(editedImageObject, designState) =>
          handleSaveEditedImage(editedImageObject, designState, editingImage)
        }
        zIndex={1500}
      />
      <AiImproveImageModal
        open={aiImproveModalOpen}
        images={aiImproveCandidateImages}
        onClose={() => setAiImproveModalOpen(false)}
        onStart={handleStartAiImprovement}
        isStarting={isStartingAiImprove}
      />
      <ImageTagEditModal
        open={Boolean(imageForTagEdit)}
        image={imageForTagEdit}
        onClose={() => setImageForTagEdit(null)}
        onSaved={handleImageTagSaved}
      />
      <MuiConfirmationModal
        open={showRemoveBgConfirm}
        title={t(messages.removeBackgroundAgainTitle())}
        description={t(messages.removeBackgroundAgainDescription())}
        handleCancel={() => setShowRemoveBgConfirm(false)}
        handleConfirm={handleConfirmRemoveBackgroundAgain}
        sx={{ zIndex: 1600 }}
      />
    </div>
  );
};

export default memo(CreateFlyerModal);
